import React from 'react';

const SHAPES = [
  { id: 'rectangle', label: 'Rectangle' },
  { id: 'circle', label: 'Circle' },
  { id: 'rounded-rectangle', label: 'Rounded\nRectangle', smallLabel: true },
  { id: 'custom', label: 'Custom' },
];

const ShapeIcon = ({ shape, active }) => {
  const stroke = active ? '#4A7C59' : '#14281D';
  if (shape === 'rectangle') {
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <rect x="10" y="10" width="80" height="80" fill="none" stroke={stroke} strokeWidth="3" />
      </svg>
    );
  }
  if (shape === 'circle') {
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <ellipse cx="50" cy="50" rx="40" ry="40" fill="none" stroke={stroke} strokeWidth="3" />
      </svg>
    );
  }
  if (shape === 'rounded-rectangle') {
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <rect x="10" y="10" width="80" height="80" rx="15" ry="15" fill="none" stroke={stroke} strokeWidth="3" />
      </svg>
    );
  }
  // custom (artwork-derived contour)
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <circle cx="50" cy="50" r="30" fill="none" stroke={stroke} strokeWidth="2" strokeDasharray="5,5" />
      <circle cx="50" cy="35" r="2" fill={stroke} />
      <circle cx="43" cy="43" r="2" fill={stroke} />
      <path d="M 40 50 Q 45 55 50 50 Q 55 45 60 50" stroke={stroke} strokeWidth="2" fill="none" />
    </svg>
  );
};

/**
 * Cutline-shape picker — 4 buttons (rectangle / circle / rounded-rect /
 * custom) with required-field validation copy. Pure presentational; the
 * parent owns `cutlineShape` state.
 */
export const CutlineShapePicker = ({ cutlineShape, setCutlineShape }) => (
  <div>
    <h2 className="text-xl font-bold text-gray-800 mb-1">Cutline</h2>
    <p className="text-gray-600 text-sm mb-4">Select your desired shape.</p>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {SHAPES.map((s) => {
        const active = cutlineShape === s.id;
        return (
          <button
            key={s.id}
            onClick={() => setCutlineShape(s.id)}
            data-testid={`cutline-shape-${s.id}`}
            className={`p-4 border-2 rounded-xl transition-all ${
              active
                ? 'border-[#4A7C59] bg-[#4A7C59]/5 shadow-sm ring-1 ring-[#4A7C59]/20'
                : 'border-slate-200 hover:border-[#4A7C59]/40'
            }`}
          >
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 mb-2 flex items-center justify-center">
                <ShapeIcon shape={s.id} active={active} />
              </div>
              {s.smallLabel ? (
                <span className="font-semibold text-gray-700 text-center text-xs leading-tight">
                  {s.label.split('\n').map((part, i) => (
                    <React.Fragment key={part}>
                      {i > 0 && <br />}
                      {part}
                    </React.Fragment>
                  ))}
                </span>
              ) : (
                <span className="font-semibold text-gray-700 text-sm">{s.label}</span>
              )}
            </div>
          </button>
        );
      })}
    </div>

    {cutlineShape === 'custom' && (
      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Custom Shape:</strong> The die-line will be auto-detected from your uploaded artwork file.
        </p>
      </div>
    )}

    {!cutlineShape && (
      <p className="mt-3 text-sm text-red-600" data-testid="cutline-required-msg">
        * Please select a cutline shape to continue
      </p>
    )}
  </div>
);

export default CutlineShapePicker;
